import React, { useState, useEffect } from 'react';
import {
  Stethoscope, CalendarCheck, Clock, Baby, HeartPulse, Scissors,
  FlaskConical, Activity, Smile, AlertCircle, Briefcase, Video, Users
} from 'lucide-react';
import { PORTAL_URL } from '../SiteLayout';
import { CMT_MINI_PHOTOS, miniPhoto } from '../data/photos';
import PageSEO from '../../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MINI_BY_TAG = CMT_MINI_PHOTOS.reduce((acc, p) => {
  (acc[p.tag] = acc[p.tag] || []).push(p);
  return acc;
}, {});

// Structure éditoriale officielle (Content Book Chapitre 4)
const MEDICAL_SERVICES = [
  {
    id: 'gyneco',
    icon: <HeartPulse size={26} />,
    title: 'Gynécologie et Obstétrique',
    tagline: 'Votre santé de la femme, notre priorité.',
    description: "Prévention, diagnostic, traitement et suivi des pathologies de la femme à toutes les étapes de sa vie.",
    features: [
      'Consultations gynécologiques',
      'Suivi de grossesse et consultations prénatales',
      'Échographie obstétricale et gynécologique',
      'Infertilité du couple',
      'Planification familiale',
      'Chirurgie gynécologique',
      'Dépistage des cancers gynécologiques',
      'Colposcopie et hystéroscopie',
    ],
  },
  {
    id: 'maternity',
    icon: <Baby size={26} />,
    title: 'Maternité',
    tagline: 'Un accompagnement sécurisé pour la mère et l\u2019enfant.',
    description: "Accompagnement des futures mamans depuis le suivi de grossesse jusqu\u2019à l\u2019accouchement et au post-partum.",
    features: [
      'Consultations prénatales',
      "Préparation à l'accouchement",
      'Accouchement',
      'Césarienne',
      'Suivi postnatal',
    ],
  },
  {
    id: 'pediatrics',
    icon: <Baby size={26} />,
    title: 'Pédiatrie',
    tagline: 'Parce que chaque enfant mérite le meilleur départ dans la vie.',
    description: "Suivi médical du nouveau-né, du nourrisson, de l\u2019enfant et de l\u2019adolescent.",
    features: [
      'Consultations pédiatriques',
      'Vaccination',
      'Suivi de croissance',
      'Urgences pédiatriques',
      'Nutrition infantile',
    ],
  },
  {
    id: 'general',
    icon: <Stethoscope size={26} />,
    title: 'Médecine Générale',
    tagline: 'Le premier contact pour votre santé.',
    description: "Diagnostic, traitement, suivi des maladies courantes et orientation vers les spécialistes lorsque cela est nécessaire.",
    features: [],
  },
  {
    id: 'surgery',
    icon: <Scissors size={26} />,
    title: 'Chirurgie',
    tagline: 'Une prise en charge chirurgicale dans un environnement sécurisé.',
    description: "Interventions programmées et urgentes selon les indications médicales.",
    features: [],
  },
];

const TECH_SERVICES = [
  {
    id: 'lab',
    icon: <FlaskConical size={26} />,
    title: "Laboratoire d'Analyses Médicales",
    description: "Un large éventail d\u2019analyses permettant un diagnostic rapide et fiable.",
    features: [
      'Hématologie',
      'Biochimie',
      'Immunologie',
      'Hormonologie',
      'Parasitologie',
      'Bactériologie',
      'Sérologie',
    ],
  },
  {
    id: 'imagerie',
    icon: <Activity size={26} />,
    title: 'Imagerie Médicale',
    description: "Diagnostic assisté par des équipements modernes.",
    features: [
      'Échographie',
      'Échographie Doppler',
      'Échographie obstétricale',
      'Échographie mammaire',
      'Échographie abdominale',
      'Mammographie (à venir)',
    ],
  },
];

const OTHER_SERVICES = [
  { id: 'dental', icon: <Smile size={22} />, title: 'Dentisterie', description: 'Prévention, soins dentaires et traitements adaptés à tous les âges.' },
  { id: 'urgences', icon: <AlertCircle size={22} />, title: 'Urgences', description: 'Une équipe disponible pour la prise en charge des situations nécessitant une intervention rapide.' },
  { id: 'workmed', icon: <Briefcase size={22} />, title: 'Médecine du Travail', description: "Suivi médical des collaborateurs d'entreprises partenaires : visites d'embauche, périodiques, aptitude, bilans, prévention." },
];

export default function SiteServices() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/services`)
      .then(r => r.json())
      .then(d => setServices((d.services || []).filter(s => !s.name.toLowerCase().includes('test'))))
      .catch(() => {});
  }, []);

  return (
    <main data-testid="site-services">
      <PageSEO
        title="Nos services médicaux — Matadi & Boma"
        description="Consultations médicales à Matadi & Boma : médecine générale, chirurgie, gynécologie-obstétrique, pédiatrie, médecine interne, dermatologie, neurologie, urologie et téléconsultation."
        path="/services"
      />
      <div className="site-page-head">
        <div className="site-page-head-inner">
          <h1 className="site-reveal">Une offre de soins complète pour répondre à tous vos besoins de santé</h1>
          <p className="site-reveal-1">
            Le Centre Médical la Trinité met à votre disposition une équipe pluridisciplinaire et des
            équipements modernes afin d&apos;assurer une prise en charge globale, coordonnée et de qualité.
            De la consultation spécialisée aux examens complémentaires, de la prévention à la chirurgie,
            nous vous accompagnons à chaque étape de votre parcours de santé.
          </p>
        </div>
      </div>

      {/* Notre approche */}
      <section className="site-section">
        <div className="site-split">
          <div>
            <span className="site-kicker">Notre approche</span>
            <h2>Une prise en charge coordonnée</h2>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
              Chaque patient bénéficie d&apos;une approche personnalisée reposant sur la collaboration entre
              les différentes spécialités médicales et les services techniques. Lorsque cela est nécessaire,
              plusieurs professionnels interviennent conjointement afin d&apos;établir le meilleur diagnostic
              et de proposer la stratégie thérapeutique la plus adaptée.
            </p>
          </div>
          <img src="/photos/cabinet_consultation.jpg" alt="Cabinet de consultation du Centre Médical la Trinité" loading="lazy" className="cmt-side-photo" />
        </div>
      </section>

      {/* Services médicaux */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Services médicaux</span>
          <h2>Nos spécialités</h2>
          <div className="site-grid" style={{ marginTop: '2rem' }}>
            {MEDICAL_SERVICES.map(s => {
              const minis = (s.id === 'surgery') ? MINI_BY_TAG['chirurgie'] || []
                           : (s.id === 'maternity') ? MINI_BY_TAG['maternite'] || []
                           : [];
              return (
              <div className="site-card" key={s.id} data-testid={`service-med-${s.id}`}>
                <div className="site-card-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p style={{ fontStyle: 'italic', color: 'var(--site-primary)', marginBottom: 8 }}>{s.tagline}</p>
                <p>{s.description}</p>
                {s.features.length > 0 && (
                  <ul style={{ marginTop: '.8rem', paddingLeft: '1.1rem', color: 'var(--site-muted)', fontSize: 14, lineHeight: 1.6 }}>
                    {s.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                )}
                {minis.length > 0 && (
                  <div className="cmt-service-minis" data-testid={`service-mini-${s.id}`}>
                    {minis.map(m => (
                      <img key={m.file} src={miniPhoto(m.file)} alt={m.caption} title={m.caption} loading="lazy" />
                    ))}
                  </div>
                )}
                <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-primary" style={{ marginTop: '1rem' }} data-testid={`service-book-${s.id}`}>
                  <CalendarCheck size={16} /> Prendre rendez-vous
                </a>
              </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Services médico-techniques */}
      <section className="site-section">
        <span className="site-kicker">Services médico-techniques</span>
        <h2>Diagnostic et analyses</h2>
        <div className="site-split" style={{ marginTop: '2rem', alignItems: 'stretch' }}>
          <div className="site-grid" style={{ gridTemplateColumns: '1fr', gap: '1.2rem' }}>
            {TECH_SERVICES.map(s => (
              <div className="site-card" key={s.id} data-testid={`service-tech-${s.id}`}>
                <div className="site-card-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
                <ul style={{ marginTop: '.8rem', paddingLeft: '1.1rem', color: 'var(--site-muted)', fontSize: 14, lineHeight: 1.6 }}>
                  {s.features.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <img src="/photos/echographie.jpg" alt="Salle d'échographie — Centre Médical la Trinité" loading="lazy" className="cmt-side-photo" />
        </div>
      </section>

      {/* Autres services */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Autres services</span>
          <h2>Une prise en charge complète</h2>
          <div className="site-split" style={{ marginTop: '2rem', alignItems: 'stretch' }}>
            <img src="/photos/chambre_hospitalisation.jpg" alt="Chambre d'hospitalisation — Centre Médical la Trinité" loading="lazy" className="cmt-side-photo" />
            <div className="site-grid" style={{ gridTemplateColumns: '1fr', gap: '1.2rem' }}>
              {OTHER_SERVICES.map(s => (
                <div className="site-card" key={s.id} data-testid={`service-other-${s.id}`}>
                  <div className="site-card-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Médecine connectée */}
      <section className="site-section">
        <div className="site-split">
          <div>
            <span className="site-kicker">Médecine connectée</span>
            <h2>Des soins qui dépassent les frontières</h2>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75, marginBottom: '1rem' }}>
              Grâce à <strong>CMT Health Platform</strong>, le Centre Médical la Trinité développe progressivement
              une nouvelle manière d&apos;accompagner les patients. Prise de rendez-vous en ligne, téléconsultation,
              dossier patient numérique et échanges sécurisés simplifient le parcours de soins.
            </p>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
              Lorsque l&apos;expertise recherchée n&apos;est pas disponible au Kongo Central, la plateforme
              facilite l&apos;accès à des spécialistes exerçant dans d&apos;autres provinces ou à l&apos;international.
            </p>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #eff8fc, #ffffff)', borderRadius: 16, padding: '2rem', textAlign: 'center' }}>
            <Video size={56} color="var(--site-primary)" />
            <h3 style={{ marginTop: '1rem' }}>Téléconsultation</h3>
            <p style={{ marginBottom: '1.5rem' }}>Consultez un médecin à distance, où que vous soyez.</p>
            <a href="/teleconsultation" className="site-btn site-btn-primary" data-testid="services-tele-btn">
              En savoir plus
            </a>
          </div>
        </div>
      </section>

      {/* Grille tarifs (actuels services API) */}
      {services.length > 0 && (
        <div className="site-section-alt-wrap">
          <section className="site-section">
            <span className="site-kicker">Consultations disponibles</span>
            <h2>Réservez votre consultation en ligne</h2>
            <div className="site-grid" data-testid="site-services-grid" style={{ marginTop: '2rem' }}>
              {services.map(s => (
                <div className="site-card" key={s.service_id} data-testid={`site-service-${s.service_id}`}>
                  <div className="site-card-icon"><Users size={22} /></div>
                  <h3>{s.name}</h3>
                  <p>{s.description}</p>
                  {s.duration_minutes && (
                    <p style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Clock size={14} /> {s.duration_minutes} minutes
                    </p>
                  )}
                  <div className="site-card-price">
                    <strong>{Number(s.price_cdf).toLocaleString('fr-FR').replace(/[\u202F\u00A0]/g, ' ')} CDF</strong>
                    {s.price_usd ? (
                      <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--site-muted)', fontWeight: 500, marginTop: 3 }}>
                        ≈ {Number(s.price_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Appel à l'action */}
      <section className="site-section">
        <div className="site-cta-band">
          <div>
            <h2>Besoin d&apos;une consultation ?</h2>
            <p>Notre équipe est à votre disposition pour vous orienter vers le service le plus adapté à votre situation.</p>
          </div>
          <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="services-cta-booking-btn">
            <CalendarCheck size={18} /> Prendre rendez-vous
          </a>
        </div>
      </section>
    </main>
  );
}
